import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays/Modal';
import { Button } from '~/components/ui';
import { useRemoveTradingPartnersFromProductComparison } from '~/api/organizations';
import { type ClientError, extractGraphQLErrorMessages } from '~/utils';

type RemoveSellerConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  platformProductId?: string;
  platformProductLengthIds?: string[];
  onRemoved?: (organizationId: string) => void;
};

export function RemoveSellerConfirmModal({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  platformProductId,
  platformProductLengthIds,
  onRemoved,
}: Readonly<RemoveSellerConfirmModalProps>) {
  const { t } = useTranslation('order-builder');
  const { mutate: removeSeller, isPending } =
    useRemoveTradingPartnersFromProductComparison();

  const handleConfirm = () => {
    removeSeller(
      {
        input: {
          organizationIds: [organizationId],
          platformProductId,
          platformProductLengthIds,
        },
      },
      {
        onSuccess: (data) => {
          if (
            data.removeTradingPartnersFromProductComparison.includes(
              organizationId
            )
          ) {
            toast.success(
              t('order_builder.remove_seller.success', {
                name: organizationName,
              })
            );
            onRemoved?.(organizationId);
            onClose();
          } else {
            toast.error(
              t('order_builder.remove_seller.error', {
                name: organizationName,
              })
            );
          }
        },
        onError: (error) =>
          toast.error(
            extractGraphQLErrorMessages(error as ClientError).join(', ')
          ),
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-full max-w-sm">
        <h2
          id="Modal-title"
          className="text-base font-semibold text-neut-prim mb-2"
        >
          {t('order_builder.remove_seller.title', { name: organizationName })}
        </h2>
        <p className="text-sm text-neut-sec mb-4">
          {t('order_builder.remove_seller.message', {
            name: organizationName,
          })}
        </p>

        <div className="flex gap-3">
          <Button
            size="sm"
            onClick={onClose}
            disabled={isPending}
            variant="accent"
            data-testid="remove-seller-cancel-button"
          >
            {t('order_builder.remove_seller.cancel')}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending}
            data-testid="remove-seller-confirm-button"
          >
            {t('order_builder.remove_seller.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
